import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../api";
import { pct, tone } from "../format";
import { Card } from "../shell/ui";
import { Icon } from "./Icon";

interface Current {
  season: { id: string; title: string; ends_at: string };
  participants: number;
  ranked: number;
  top: Array<{ bot_id: string; bot_name: string; rank: number; return_pct: number; owner: string | null }>;
  /** rank null: in the season, but no closed trade in it yet. */
  mine: Array<{ bot_id: string; bot_name: string; rank: number | null; return_pct: number }>;
  myCount: number;
}

function daysLeft(iso: string): string {
  const d = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  return d <= 1 ? "ends today" : `${d} days left`;
}

/** This month's league in a card: the top three and, for a member, their own best bots. */
export function LeagueCurrent() {
  const q = useQuery({
    queryKey: ["league-current"],
    queryFn: () => api<Current>("/hub/league/current"),
    refetchInterval: 60_000,
  });
  const d = q.data;
  if (!d) return null;
  return (
    <Card
      title={
        <span className="card-title-icon">
          <Icon name="league" /> League · {d.season.title}
        </span>
      }
      actions={
        <Link className="btn btn--sm btn--ghost" to={`/league/${d.season.id}`}>
          Standings
        </Link>
      }
    >
      <div className="stack league-current">
        <p className="small muted">
          {d.participants} public bots compete, {d.ranked} ranked so far · {daysLeft(d.season.ends_at)}
        </p>
        <ol className="league-current__list">
          {d.top.map((r) => (
            <li key={r.bot_id}>
              <span className="league-current__rank">#{r.rank}</span>
              <Link to={`/b/${r.bot_id}`}>{r.bot_name}</Link>
              {r.owner && <span className="muted small">@{r.owner}</span>}
              <span className={`league-current__ret ${tone(r.return_pct)}`}>{pct(r.return_pct, 2)}</span>
            </li>
          ))}
        </ol>
        {d.myCount > 0 ? (
          <p className="small">
            <strong>Your best:</strong>{" "}
            {d.mine.map((r, i) => (
              <span key={r.bot_id}>
                {i > 0 && " · "}
                {r.rank === null ? (
                  <span className="muted" title="Ranked after its first closed trade in the season">
                    unranked
                  </span>
                ) : (
                  `#${r.rank}`
                )}{" "}
                <Link to={`/b/${r.bot_id}`}>{r.bot_name}</Link>
              </span>
            ))}
            {d.myCount > d.mine.length && <span className="muted"> (+{d.myCount - d.mine.length} more)</span>}
          </p>
        ) : (
          <p className="small muted">Every public paper bot takes part automatically.</p>
        )}
      </div>
    </Card>
  );
}
